import { useCallback, useState } from 'react';
import axios from 'axios';

import useClaimStageConfigurations from '~/components/ClaimStages/hooks/useClaimStageConfigurations';
import { useSnackbar } from '~/components/core/Molecules/Snackbar';
import useOrganization from '~/components/OrganizationContext';
import type {
  ClaimStageConfigurationCreateJson,
  ClaimStageConfigurationUpdateJson,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import {
  createClaimStageConfiguration,
  moveClaimStageConfiguration,
  updateClaimStageConfiguration,
} from '../apiService';
import TEXTS from '../texts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BaseUpdateFunc = (organizationId: number, ...args: any) => Promise<any>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TailParams<T> = T extends (organizationId: number, ...args: infer P) => Promise<any> ? P : never;

const useClaimStageConfigurationAndMutations = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();
  const { claimStageConfigurations, isLoading, isError, reloadData } = useClaimStageConfigurations();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organizationId } = useOrganization();

  const updateData = useCallback(
    async <UpdateFunc extends BaseUpdateFunc>(updateFn: UpdateFunc, ...args: TailParams<UpdateFunc>) => {
      setIsUpdating(true);
      try {
        await updateFn(organizationId, ...args);
        await reloadData();
      } catch (error) {
        reportAxiosError(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [organizationId, reloadData]
  );

  const create = useCallback(
    async (values: ClaimStageConfigurationCreateJson) => {
      await updateData(createClaimStageConfiguration, values);
    },
    [updateData]
  );

  const update = useCallback(
    async (stageKey: string, values: ClaimStageConfigurationUpdateJson) => {
      await updateData(updateClaimStageConfiguration, stageKey, values);
    },
    [updateData]
  );

  const reorder = useCallback(
    async (stageKey: string, fromIndex: number, toStageKey: string, toIndex: number): Promise<boolean> => {
      setIsUpdating(true);
      try {
        await moveClaimStageConfiguration(organizationId, stageKey, fromIndex, toStageKey, toIndex);
        return true;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 409) {
          showSnackbar(TEXTS.listChangedOnMove, 'warning');
        } else {
          reportAxiosError(error);
        }
        return false;
      } finally {
        // Resync to the server's order (reverts on failure) and release the saving flag
        // even if the resync itself throws, so the UI can't get stuck disabled.
        try {
          await reloadData();
        } finally {
          setIsUpdating(false);
        }
      }
    },
    [organizationId, reloadData, showSnackbar]
  );

  return {
    claimStageConfigurations,
    isUpdating,
    isLoading,
    isError,
    reloadData,
    create,
    update,
    reorder,
    snackbarState,
    closeSnackbar,
  };
};

export default useClaimStageConfigurationAndMutations;
